import React, { useState, useEffect } from 'react';
import { Card, Alert } from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';
import { LanguageSwitcher } from '../../components/shared';
import './PatientQuestionnaire.css';

export function QuestionnaireComplete() {
  const [searchParams] = useSearchParams();
  
  // Language state - detect from URL or default to Greek for patients
  const [language, setLanguage] = useState<'en' | 'el'>('el');

  // Detect language from URL parameters, sessionStorage, or localStorage on component mount
  useEffect(() => {
    const urlLang = searchParams.get('lang');
    const sessionLang = sessionStorage.getItem('questionnaireCompletionLanguage');
    const savedLang = localStorage.getItem('patientQuestionnaireLanguage');
    
    if (urlLang === 'en' || urlLang === 'el') {
      setLanguage(urlLang);
    } else if (sessionLang === 'en' || sessionLang === 'el') {
      setLanguage(sessionLang);
    } else if (savedLang === 'en' || savedLang === 'el') {
      setLanguage(savedLang);
    }
    
    // Clean up sessionStorage after using it
    if (sessionLang) {
      sessionStorage.removeItem('questionnaireCompletionLanguage');
    }
  }, [searchParams]);

  // Save language preference when it changes
  useEffect(() => {
    localStorage.setItem('patientQuestionnaireLanguage', language);
  }, [language]);

  // Translation helper
  const t = (key: string): string => {
    const translations: Record<string, { el: string; en: string }> = {
      // Main heading
      questionnaireComplete: { 
        el: 'Το Ερωτηματολόγιό σας Ολοκληρώθηκε!', 
        en: 'Questionnaire Complete!' 
      },
      
      // Thank you section
      thankYouTitle: { 
        el: 'Ευχαριστούμε για τη Συμμετοχή σας', 
        en: 'Thank You for Your Participation' 
      },
      thankYouMessage: { 
        el: 'Οι απαντήσεις σας έχουν υποβληθεί και αποθηκευτεί με ασφάλεια. Ο γιατρός σας θα εξετάσει τις απαντήσεις σας για να σας παρέχει την καλύτερη δυνατή φροντίδα.', 
        en: 'Your responses have been successfully submitted and securely saved. Your doctor will review your answers to provide you with the best possible care.' 
      },
      
      // Review section
      reviewTitle: { 
        el: 'Επισκόπηση Ολοκληρωμένου Ερωτηματολογίου', 
        en: 'Completed Questionnaire Review' 
      },
      reviewMessage: { 
        el: 'Το ερωτηματολόγιο έχει ολοκληρωθεί και οι απαντήσεις σας είναι διαθέσιμες για επισκόπηση από τον πάροχο υγειονομικής περίθαλψής σας.', 
        en: 'The questionnaire has been completed and your responses are available for review by your healthcare provider.' 
      },
      
      // Close window message
      closeWindowMessage: { 
        el: 'Μπορείτε να κλείσετε αυτό το παράθυρο με ασφάλεια τώρα. Σας ευχαριστούμε για τον χρόνο σας!', 
        en: 'You can safely close this window now. Thank you for your time!' 
      }
    };
    
    return translations[key]?.[language] || key;
  };

  return (
    <div className="questionnaire-complete-container">
      <div className="container mt-5">
        <div className="row justify-content-center">
          <div className="col-md-8 col-lg-6">
            {/* Language switcher using universal component */}
            <div className="d-flex justify-content-end mb-3">
              <LanguageSwitcher
                selectedLanguage={language}
                onLanguageChange={setLanguage}
                variant="compact"
                className="questionnaire-complete-lang-switcher"
              />
            </div>
            
            <Card className="shadow-lg border-0">
              <Card.Body className="text-center p-5">
                <div className="mb-4">
                  <div className="success-icon-wrapper">
                    <i className="bi bi-check-circle-fill text-success" style={{ fontSize: '5rem' }}></i>
                  </div>
                </div>
                
                <h1 className="mb-3 text-success fw-bold">
                  <i className="bi bi-clipboard-check me-2"></i>
                  {t('questionnaireComplete')}
                </h1>
                
                <Alert variant="success" className="mb-4 border-0" style={{ backgroundColor: '#d1edff' }}>
                  <h5 className="mb-3 text-primary">
                    <i className="bi bi-check-circle-fill me-2"></i>
                    {t('thankYouTitle')}
                  </h5>
                  <p className="mb-2 text-dark">
                    {t('thankYouMessage')}
                  </p>
                  <div className="mt-3 p-3 bg-white rounded border-start border-4 border-info">
                    <h6 className="text-info mb-2">
                      <i className="bi bi-clipboard-data me-2"></i>
                      {t('reviewTitle')}
                    </h6>
                    <p className="mb-0 text-dark small">
                      {t('reviewMessage')}
                    </p>
                  </div>
                </Alert>
              
              <div className="text-center">
                <div className="p-3 bg-light rounded">
                  <p className="mb-0 text-muted">
                    <i className="bi bi-info-circle me-2"></i>
                    {t('closeWindowMessage')}
                  </p>
                </div>
              </div>
            </Card.Body>
          </Card>
        </div>
      </div>
      </div>
    </div>
  );
}